import * as React from 'react'

import { Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  BulkDestroyLegalEntitySupplementsDocument,
  RemoveLegalEntityPageDocument,
  UpdateEmployeeEmployeeGroupDocument
} from 'generated/resources/graphql'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import LegalEntitySelectForm from 'modules/legal_entities/lib/forms/LegalEntitySelectForm'

import RemoveLegalEntityWithDependencies from './RemoveLegalEntityWithDependencies'
import FinanceAlert from './_FinanceAlert'

type ModalProps = React.ComponentProps<typeof Modal>

interface Props {
  onClose: ModalProps['onClose']
  legalEntityId: string
}

const RemoveLegalEntity = ({ onClose, legalEntityId }: Props) => {
  const { data, loading } = useQuery(RemoveLegalEntityPageDocument, {
    variables: {
      legalEntityId
    }
  })

  const supplements = data?.payroll.supplements ?? []
  const onboardingsCount = data?.banking.onboardingsConnection.totalCount ?? 0
  const financialDocumentsCount = data?.finance.financialDocumentsConnection.totalCount ?? 0
  const allLegalEntities = data?.companies.legalEntities ?? []

  const [openModal, setOpenModal] = React.useState(false)
  const form = React.useRef(new LegalEntitySelectForm({})).current

  const [updateEmployeeEmployeeGroup] = useMutation(UpdateEmployeeEmployeeGroupDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee
  })

  const [bulkDestroySupplements] = useMutation(BulkDestroyLegalEntitySupplementsDocument, {
    extractorFn: (data) => data.payrollMutations.bulkDestroyLegalEntitySupplementsPayrollSupplement
  })

  const legalEntity = React.useMemo(() => {
    return allLegalEntities.find((le) => le.id === legalEntityId)
  }, [allLegalEntities, legalEntityId])

  const currentLegalEntityEmployeeIds = React.useMemo(() => {
    if (!legalEntity) return []

    return legalEntity.employeeGroups.flatMap((group) => group.employees.map((e) => e.id))
  }, [legalEntity])

  const options = React.useMemo(() => {
    if (!legalEntity) return []

    return allLegalEntities
      .filter((le) => le.id !== legalEntityId)
      .filter((le) => le.employeeGroups.length > 0)
      .map((le) => ({
        label: le.legalName,
        value: le.id
      }))
  }, [allLegalEntities, legalEntity, legalEntityId])

  const newLegalEntityId = String(form.get('legal_entity_id').value)

  const employeeGroupOptions = React.useMemo(() => {
    const newLegalEntity = allLegalEntities.find((le) => le.id === newLegalEntityId)

    if (!newLegalEntity) return []

    return newLegalEntity.employeeGroups.map((group) => ({
      label: group.name,
      value: group.id
    }))
  }, [allLegalEntities, newLegalEntityId])

  const newEmployeeGroupId = String(form.get('employee_group_id').value)
  const blockedByFinance = financialDocumentsCount > 0 || onboardingsCount > 0

  const disabled = React.useMemo(() => {
    if (!legalEntity) return true

    if (allLegalEntities.length < 2) return true

    if (blockedByFinance) return true

    const filled = !!newLegalEntityId && !!newEmployeeGroupId

    return !!currentLegalEntityEmployeeIds.length && !filled
  }, [
    legalEntity,
    allLegalEntities.length,
    newLegalEntityId,
    newEmployeeGroupId,
    blockedByFinance,
    currentLegalEntityEmployeeIds.length
  ])

  const hasSupplements = supplements.length > 0

  const { running: destroying, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (hasSupplements) {
        await bulkDestroySupplements({
          variables: {
            legalEntityId
          }
        })
      }

      if (currentLegalEntityEmployeeIds.length) {
        const promises = currentLegalEntityEmployeeIds.map((employeeId) =>
          updateEmployeeEmployeeGroup({
            variables: {
              id: employeeId,
              legalEntityId: newLegalEntityId,
              employeeGroupId: newEmployeeGroupId
            }
          })
        )
        await Promise.all(promises)
      }

      // TODO: Replace with GraphQL mutation when destroyCompaniesLegalEntity is available in the schema
      await ApiClient().del(`/api/v2/legal_entities/${legalEntityId}`)
      setOpenModal(false)
      onClose && onClose()
      closePortal()
    }
  )

  if (!legalEntity) return null

  const hasEmployees = !!currentLegalEntityEmployeeIds.length

  const description = hasEmployees
    ? i18n.t('settings.legal_entities.legal_entity.remove.with_employees')
    : i18n.t('settings.legal_entities.legal_entity.remove.description')

  return (
    <>
      <Modal
        illustration="alert"
        title={i18n.t('settings.legal_entities.legal_entity.remove.title', {
          legal_entity: legalEntity.legalName
        })}
        description={blockedByFinance ? null : description}
        onClose={onClose}
        onSubmit={onSubmit}
        footer={
          loading ? (
            <Spinner />
          ) : hasSupplements ? (
            <>
              <Button type="secondary" onClick={onClose}>
                {i18n.t('settings.legal_entities.legal_entity.remove.cancel')}
              </Button>
              <Button
                disabled={disabled || destroying}
                type="negative"
                onClick={() => {
                  setOpenModal(true)
                }}
              >
                {i18n.t('settings.legal_entities.legal_entity.remove.next')}
              </Button>
            </>
          ) : (
            <Button disabled={disabled || destroying} type="negative" submit>
              {i18n.t('settings.legal_entities.legal_entity.remove.submit')}
            </Button>
          )
        }
      >
        {blockedByFinance && (
          <FinanceAlert
            financialDocumentsCount={financialDocumentsCount}
            bankAccountsCount={onboardingsCount}
          />
        )}

        {hasEmployees && !blockedByFinance && (
          <>
            <FormRow>
              <Select
                none={i18n.t('common.none')}
                field={form.get('legal_entity_id')}
                label={i18n.t('common.legal_entity')}
                options={options}
              />
            </FormRow>
            {newLegalEntityId !== '' && (
              <FormRow>
                <Select
                  none={i18n.t('common.none')}
                  field={form.get('employee_group_id')}
                  label={i18n.t('common.employee_group')}
                  options={employeeGroupOptions}
                />
              </FormRow>
            )}
          </>
        )}
      </Modal>
      {openModal && (
        <RemoveLegalEntityWithDependencies
          supplements={supplements}
          legalEntityName={legalEntity.legalName}
          legalEntityCurrency={legalEntity.currency}
          onClose={() => setOpenModal(false)}
          onSubmit={onSubmit}
        />
      )}
    </>
  )
}

export default RemoveLegalEntity
